import { BalloonEventModel } from 'ts-core';
import { DescriptionType } from 'ts-core';
import React, { Suspense, lazy } from 'react';
import { Info, useTheme, useTranslations } from '../..';
import { ContactInfoList } from './ContactInfoList';

// Lazy-loaded so `mapbox-gl` is code-split out of the always-loaded main chunk
// and only downloads when a micro-map is actually rendered.
const MicroMap = lazy(() => import('./MicroMap'));

export type LocationSectionVariant = 'preview' | 'full';

export interface LocationSectionProps {
    event: BalloonEventModel;
    showMap?: boolean;
    /**
     * `'preview'` (default): interactive micro-map with Mapbox **cooperative
     *   gestures** — requires two fingers to pan on touch and Ctrl/Cmd+scroll
     *   to zoom. This avoids the mobile scroll-trap where a one-finger swipe
     *   starting on the map would pan the map instead of scrolling the page.
     *   Mapbox renders its own hint overlay when the user tries the wrong
     *   gesture, so no custom overlay is needed.
     *
     * `'full'`: standard interactive map with a NavigationControl. Used when
     *   the map is the primary surface (e.g. the full map view).
     */
    variant?: LocationSectionVariant;
    trackingSource?: string;
    /**
     * Optional replacement for the built-in Mapbox micro-map. When provided (and
     * the event has a valid location and `showMap` is set), this is rendered in
     * the same sized box instead of `MicroMap`. The site uses it to inject the
     * full balloon mini-map; keeps `react-ui` decoupled from the `map` package.
     */
    locationMapSlot?: React.ReactNode;
}

// NOTE: This component must never add a `mapboxgl.GeolocateControl` — detail
// pages must not prompt for geolocation on load (story 4.1).

export function LocationSection({
                                    event,
                                    showMap,
                                    variant = 'preview',
                                    locationMapSlot,
                                }: LocationSectionProps) {
    const theme = useTheme();
    const { language } = useTranslations();
    const venueInfos = [event.getDescription(DescriptionType.Venues), event.getDescription(DescriptionType.Venues2)];

    const hasValidLocation = event.location.lat !== 0 && event.location.lng !== 0;
    const isPreview = variant === 'preview';

    return event.location && Object.values(event.location).some(v => v !== null) ? (
        <div className='balloon-section location-section flex flex-col gap-4'>
            <div className='flex flex-row items-center'>
                <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth='1.5'
                    stroke='currentColor'
                    className='w-6 h-6 mx-2'
                    style={{ color: theme.primaryColor }}
                >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M15 10.5a3 3 0 11-6 0 3 3 0 016 0z'
                    />
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z'
                    />
                </svg>

                <div className='flex flex-col flex-1'>
                    <span>{event.locationName}</span>
                    <span className='text-gray-500'>{event.addressString}</span>
                </div>
            </div>

            {hasValidLocation && showMap && (
                locationMapSlot ? (
                    <div className='w-full h-[150px] md:h-64 rounded-lg overflow-hidden shadow-md relative'>
                        {locationMapSlot}
                    </div>
                ) : (
                    <Suspense fallback={<div className='w-full h-[150px] md:h-64 rounded-lg' />}>
                        <MicroMap
                            lng={event.location.lng}
                            lat={event.location.lat}
                            primaryColor={theme.primaryColor}
                            isPreview={isPreview}
                        />
                    </Suspense>
                )
            )}

            {venueInfos.map((v, i) => v && <Info key={i} info={v.getText(language)} />)}
            <ContactInfoList contacts={event.fullLocation?.contactInfos ?? []} className='pl-4' />
        </div>
    ) : null;
}
